import React from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from './contexts/ThemeContext';
import { Meteors } from './components/ui/meteors';
import { CardSpotlight } from "./components/ui/card-spotlight";
import { motion, AnimatePresence } from "framer-motion";
import './index.css';
import { StarryNight } from "./components/ui/starry-night";
import { Compare } from "./components/ui/compare";
import { TextHoverEffect } from "./components/ui/text-hover-effect";
import { TextHoverEffectSEO } from "./components/ui/text-hover-effect-seo";
import ServicesPage from './components/ServicesPage';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import PortfolioPage from './components/PortfolioPage';
import AdminPage from './components/AdminPage';
import ClientPage from './components/ClientPage';
import ProductPage from './components/ProductPage';
import AuthModal from './components/AuthModal';
import CartDrawer from './components/CartDrawer';
import { isSupabaseConfigured, supabase } from './lib/supabase';
import { IconMenu2, IconX, IconSun, IconMoon } from '@tabler/icons-react';
import { useTheme } from './contexts/ThemeContext';

// ---------------------------------------------------------------------------
// RouteSEO: keeps document.title, meta description, canonical, OG & Twitter
// tags in sync with the current route. Renders nothing; purely head-side SEO.
// ---------------------------------------------------------------------------
const SITE_URL = 'https://designdotdev.co.za';
const SITE_NAME = 'DesignDotDev';
const DEFAULT_OG_IMAGE = `${SITE_URL}/DDDlogopreviewcard.png`;

const ROUTE_META = {
  '/': {
    title: 'DesignDotDev — Web Design & Development in South Africa',
    description:
      'Boutique web design and development studio in South Africa. Fast, accessible, beautiful digital experiences for startups and SMEs — branding, websites, SEO, and custom web apps.',
    robots: 'index, follow, max-image-preview:large',
  },
  '/about': {
    title: 'About DesignDotDev — Our Story, Values & Process',
    description:
      'Meet DesignDotDev — a South African web design and development studio obsessed with craft, speed, and accessibility. Learn about our team, values, and design process.',
    robots: 'index, follow, max-image-preview:large',
  },
  '/services': {
    title: 'Services — Web Design, Development, Branding & SEO | DesignDotDev',
    description:
      'Custom websites, web apps, brand identity, UI/UX, and SEO engineered for ranking. Explore the services DesignDotDev offers startups and SMEs across South Africa.',
    robots: 'index, follow, max-image-preview:large',
  },
  '/portfolio': {
    title: 'Portfolio — Selected Work & Case Studies | DesignDotDev',
    description:
      'A selection of recent websites, web apps, and brand work by DesignDotDev. Explore case studies that showcase fast, accessible, and beautifully crafted digital products.',
    robots: 'index, follow, max-image-preview:large',
  },
  '/contact': {
    title: 'Contact DesignDotDev — Start Your Project',
    description:
      'Tell us about your project. Get in touch with DesignDotDev for web design, development, branding, and SEO services in South Africa.',
    robots: 'index, follow, max-image-preview:large',
  },
  '/product': {
    title: 'Website Packages — Buy a Bespoke Website | DesignDotDev',
    description:
      'Buy a bespoke DesignDotDev website monthly or once-off. Launch, Growth, and premium AI business platform tiers with clear pricing and modular bolt-ons.',
    robots: 'index, follow, max-image-preview:large',
  },
  '/order': {
    title: 'Order a Website — DesignDotDev',
    description:
      'Start a bespoke website order with DesignDotDev. Choose a package, create a client account, and begin the AI-assisted project intake.',
    robots: 'index, follow, max-image-preview:large',
  },
  '/admin': {
    title: 'Admin — DesignDotDev',
    description: 'Private administration area.',
    robots: 'noindex, nofollow, noarchive, nosnippet',
  },
  '/client': {
    title: 'Client Portal — DesignDotDev',
    description: 'Private DesignDotDev client portal for account access and project updates.',
    robots: 'noindex, nofollow, noarchive, nosnippet',
  },
  '__notFound': {
    title: 'Page not found — DesignDotDev',
    description: 'The page you are looking for does not exist. Return to DesignDotDev to explore our web design and development work.',
    robots: 'noindex, follow',
  },
};

const upsertMeta = (selector, attrs) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => {
      if (k !== 'content') el.setAttribute(k, v);
    });
    document.head.appendChild(el);
  }
  if (attrs.content != null) el.setAttribute('content', attrs.content);
};

const upsertLink = (rel, href, extra = {}) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
};

const NotFoundPage = () => (
  <main
    className="min-h-screen w-full flex flex-col items-center justify-center px-6 text-center"
    style={{ backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
  >
    <p className="font-press-start text-sm md:text-base mb-6" style={{ color: 'var(--accent-primary)' }}>
      404
    </p>
    <h1 className="text-4xl md:text-6xl font-bold mb-4 tracking-tight">Page not found</h1>
    <p className="text-base md:text-lg max-w-xl mb-8" style={{ color: 'var(--text-secondary)' }}>
      The page you&rsquo;re looking for doesn&rsquo;t exist or has been moved. Let&rsquo;s get you back on track.
    </p>
    <div className="flex flex-wrap gap-3 justify-center">
      <Link
        to="/"
        className="px-5 py-2 rounded-md font-medium transition-opacity hover:opacity-80"
        style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
      >
        Back to home
      </Link>
      <Link
        to="/contact"
        className="px-5 py-2 rounded-md font-medium border transition-colors"
        style={{ borderColor: 'var(--text-primary)', color: 'var(--text-primary)' }}
      >
        Contact us
      </Link>
    </div>
  </main>
);

const RouteSEO = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    const key = pathname.replace(/\/+$/, '') || '/';
    const isKnown = Object.prototype.hasOwnProperty.call(ROUTE_META, key);
    const meta = isKnown ? ROUTE_META[key] : ROUTE_META['__notFound'];
    const url = `${SITE_URL}${key === '/' ? '/' : key}`;

    document.title = meta.title;

    upsertMeta('meta[name="description"]', { name: 'description', content: meta.description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: meta.robots });

    upsertLink('canonical', url);

    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: meta.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: meta.description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_OG_IMAGE });

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: meta.title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: meta.description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: DEFAULT_OG_IMAGE });
    upsertMeta('meta[name="twitter:url"]', { name: 'twitter:url', content: url });
  }, [pathname]);

  return null;
};

// Simple hook to detect media query matches (used for mobile landscape offset)
const useMediaQuery = (query) => {
  const [matches, setMatches] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handleChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
    } else if (mql.addListener) {
      mql.addListener(handleChange);
    }
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handleChange);
      } else if (mql.removeListener) {
        mql.removeListener(handleChange);
      }
    };
  }, [query]);
  return matches;
};

const App = () => {
  const { theme, toggleTheme } = useTheme();
  const [cardsDropped, setCardsDropped] = React.useState(false);
  const [showBlackBg, setShowBlackBg] = React.useState(false);
  const [activeOverlay, setActiveOverlay] = React.useState('none'); // 'none' | 'cleanCode' | 'creativeDesign'
  const [showRocketVideo, setShowRocketVideo] = React.useState(false);
  const [showCreativeContent, setShowCreativeContent] = React.useState(false);
  // Add state to track if vortex overlay is active
  const [vortexActive, setVortexActive] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isClientLoggedIn, setIsClientLoggedIn] = React.useState(false);
  const [clientName, setClientName] = React.useState('');
  const [isAuthModalOpen, setIsAuthModalOpen] = React.useState(false);
  const [authModalMode, setAuthModalMode] = React.useState('login');
  const [isCartOpen, setIsCartOpen] = React.useState(false);
  const [siteCart, setSiteCart] = React.useState(() => {
    try {
      const saved = window.localStorage.getItem('ddd-site-cart');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const isMobileLandscape = useMediaQuery('(orientation: landscape) and (max-width: 1024px)');
  const isSmallScreen = useMediaQuery('(max-width: 768px)');
  const contentTopOffsetPx = isMobileLandscape ? 96 : (isSmallScreen ? 80 : 0);
  const isProductRoute = typeof window !== 'undefined' && ['/product', '/order'].includes(window.location.pathname);
  const isHomeRoute = typeof window !== 'undefined' && window.location.pathname === '/';
  const isLightHome = isHomeRoute && theme === 'light';
  const navTextColor = vortexActive || isProductRoute ? 'white' : isLightHome ? '#082f49' : isHomeRoute ? 'white' : 'var(--text-primary)';
  const useDarkNavControls = vortexActive || isProductRoute || (isHomeRoute && !isLightHome);
  const useTealLogo = theme === 'light';
  const homeShellStyle = theme === 'light'
    ? {
        background:
          'radial-gradient(circle at 76% 8%, rgba(20,184,166,0.26), transparent 32%), radial-gradient(circle at 18% 70%, rgba(56,189,248,0.14), transparent 30%), linear-gradient(135deg, #f8fafc 0%, #eef7f8 48%, #f6fbfb 100%)',
        '--text-primary': '#06131a',
        '--text-secondary': 'rgba(8,47,73,0.66)',
        '--accent-primary': '#0f766e',
        '--accent-secondary': '#38bdf8',
        '--border-primary': 'rgba(20,184,166,0.3)',
        '--card-bg': 'rgba(255,255,255,0.72)',
      }
    : {
        background:
          'radial-gradient(circle at 76% 8%, rgba(249,115,22,0.26), transparent 32%), radial-gradient(circle at 18% 70%, rgba(251,146,60,0.12), transparent 30%), linear-gradient(135deg, #050505 0%, #0b0b0d 52%, #17100b 100%)',
        '--text-primary': '#fff7ed',
        '--text-secondary': 'rgba(255,237,213,0.68)',
        '--accent-primary': '#f97316',
        '--accent-secondary': '#f59e0b',
        '--border-primary': 'rgba(251,146,60,0.28)',
        '--card-bg': 'rgba(8,7,6,0.72)',
      };
  const homeGlowClass = theme === 'light' ? 'bg-teal-400/20' : 'bg-orange-500/10';
  const homeBadgeClass = theme === 'light'
    ? 'pointer-events-none mx-auto mb-5 inline-flex rounded-full border border-teal-400/35 bg-white/70 px-4 py-2 text-[11px] font-black uppercase tracking-[0.28em] text-teal-700 shadow-[0_0_28px_rgba(20,184,166,0.16)] backdrop-blur-xl'
    : 'pointer-events-none mx-auto mb-5 inline-flex rounded-full border border-orange-300/25 bg-orange-500/[0.08] px-4 py-2 text-[11px] font-black uppercase tracking-[0.28em] text-orange-300 shadow-[0_0_28px_rgba(249,115,22,0.12)]';
  const homeChipClass = theme === 'light'
    ? 'rounded-full border border-teal-500/20 bg-white/70 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-sky-950/65 shadow-[0_10px_30px_rgba(8,47,73,0.06)] backdrop-blur-xl'
    : 'rounded-full border border-orange-300/20 bg-black/25 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-orange-100/65';
  const homeSpotlightClass = theme === 'light'
    ? 'aspect-square !rounded-none !border-teal-400/25 !bg-white !bg-none !p-0 !shadow-[0_0_42px_rgba(20,184,166,0.14),0_20px_70px_rgba(8,47,73,0.08)]'
    : 'aspect-square !rounded-none !border-orange-300/20 !bg-[#050505] !bg-none !p-0 !shadow-[0_0_40px_rgba(249,115,22,0.14)]';
  const homeCardWashClass = theme === 'light'
    ? 'pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(20,184,166,0.18),transparent_34%)] opacity-80'
    : 'pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(249,115,22,0.16),transparent_34%)] opacity-70';
  const homeCardLineClass = theme === 'light'
    ? 'pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-teal-300/80 to-transparent'
    : 'pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-orange-200/70 to-transparent';
  const homeIconShadow = theme === 'light' ? '0 0 22px rgba(20,184,166,0.24)' : '0 0 22px rgba(249,115,22,0.24)';
  const moduleOverlayBackground = theme === 'light'
    ? 'radial-gradient(circle at 78% 8%, rgba(20,184,166,0.22), transparent 34%), radial-gradient(circle at 16% 76%, rgba(56,189,248,0.12), transparent 28%), linear-gradient(135deg, #f8fafc 0%, #eef7f8 50%, #f6fbfb 100%)'
    : 'radial-gradient(circle at 78% 8%, rgba(249,115,22,0.18), transparent 34%), radial-gradient(circle at 16% 76%, rgba(251,146,60,0.1), transparent 28%), linear-gradient(135deg, #050505 0%, #0b0b0d 50%, #17100b 100%)';
  const moduleTextColor = theme === 'light' ? '#082f49' : '#fff7ed';
  const moduleAccentTextClass = theme === 'light'
    ? 'text-teal-700 drop-shadow-[0_0_22px_rgba(20,184,166,0.16)]'
    : 'text-orange-300 drop-shadow-[0_0_24px_rgba(249,115,22,0.22)]';
  const moduleBodyTextClass = theme === 'light' ? 'text-sky-950/68' : 'text-orange-100/70';
  const moduleListTextClass = theme === 'light' ? 'text-sky-950/58' : 'text-orange-100/60';
  const modulePanelClass = theme === 'light'
    ? 'relative w-full max-w-3xl border border-teal-400/28 bg-white/78 p-6 shadow-[0_0_54px_rgba(20,184,166,0.16),inset_0_1px_0_rgba(125,211,252,0.3)] backdrop-blur-xl md:p-8'
    : 'relative w-full max-w-3xl border border-orange-300/28 bg-[#050505]/78 p-6 shadow-[0_0_54px_rgba(249,115,22,0.18),inset_0_1px_0_rgba(251,146,60,0.18)] backdrop-blur-xl md:p-8';
  const modulePanelLineClass = theme === 'light'
    ? 'pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-teal-300/85 to-transparent'
    : 'pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-orange-200/80 to-transparent';
  const modulePanelWashClass = theme === 'light'
    ? 'pointer-events-none absolute -inset-px bg-[radial-gradient(circle_at_82%_0%,rgba(20,184,166,0.14),transparent_34%)]'
    : 'pointer-events-none absolute -inset-px bg-[radial-gradient(circle_at_82%_0%,rgba(249,115,22,0.16),transparent_34%)]';
  const moduleReturnButtonClass = theme === 'light'
    ? 'fixed right-6 top-20 z-50 border border-teal-400/35 bg-white/70 px-6 py-3 font-black uppercase tracking-[0.14em] text-teal-800 backdrop-blur-sm transition-all duration-300 hover:border-teal-500/70 hover:bg-teal-50'
    : 'fixed right-6 top-20 z-50 border border-orange-300/35 bg-orange-500/10 px-6 py-3 font-black uppercase tracking-[0.14em] text-orange-100 backdrop-blur-sm transition-all duration-300 hover:border-orange-200/80 hover:bg-orange-500/20';
  const moduleCompareClass = theme === 'light'
    ? 'w-[320px] h-[320px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] border border-teal-400/30 bg-white/45 shadow-[0_0_48px_rgba(20,184,166,0.14)] ring-1 ring-sky-200/40 backdrop-blur-xl'
    : 'w-[320px] h-[320px] md:w-[400px] md:h-[400px] lg:w-[500px] lg:h-[500px] border border-orange-300/30 bg-black/35 shadow-[0_0_48px_rgba(249,115,22,0.16)] ring-1 ring-orange-200/10 backdrop-blur-xl';
  const moduleFrameClass = theme === 'light' ? 'pointer-events-none absolute inset-[-10px] border border-teal-400/14' : 'pointer-events-none absolute inset-[-10px] border border-orange-300/12';
  const moduleFrameLineClass = theme === 'light'
    ? 'pointer-events-none absolute inset-x-6 -top-2 h-px bg-gradient-to-r from-transparent via-teal-300/75 to-transparent'
    : 'pointer-events-none absolute inset-x-6 -top-2 h-px bg-gradient-to-r from-transparent via-orange-200/70 to-transparent';
  const moduleHoverGlowClass = theme === 'light'
    ? 'pointer-events-none absolute inset-x-12 top-1/2 h-24 -translate-y-1/2 bg-teal-400/14 blur-3xl'
    : 'pointer-events-none absolute inset-x-12 top-1/2 h-24 -translate-y-1/2 bg-orange-500/10 blur-3xl';
  const creativeTitleClass = theme === 'light'
    ? 'mb-6 bg-gradient-to-r from-teal-700 via-sky-500 to-cyan-400 bg-clip-text text-4xl font-black text-transparent md:text-6xl'
    : 'mb-6 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300 bg-clip-text text-4xl font-black text-transparent md:text-6xl';
  const creativeCardClass = theme === 'light'
    ? 'border border-teal-400/24 bg-white/34 p-6 text-sky-950 shadow-[0_0_34px_rgba(20,184,166,0.1),inset_0_1px_0_rgba(255,255,255,0.32)] backdrop-blur-2xl'
    : 'border border-orange-300/20 bg-black/24 p-6 text-orange-50 shadow-[0_0_34px_rgba(249,115,22,0.1),inset_0_1px_0_rgba(255,237,213,0.08)] backdrop-blur-2xl';
  const creativeIconClass = theme === 'light'
    ? 'mx-auto mb-4 flex h-12 w-12 items-center justify-center bg-gradient-to-r from-teal-500 to-sky-400 shadow-[0_0_18px_rgba(20,184,166,0.26)]'
    : 'mx-auto mb-4 flex h-12 w-12 items-center justify-center bg-gradient-to-r from-orange-500 to-amber-400 shadow-[0_0_18px_rgba(249,115,22,0.28)]';
  const creativeMutedTextClass = theme === 'light' ? 'text-sm text-sky-950/58' : 'text-sm text-orange-100/58';
  const creativeSpecialtyPanelClass = theme === 'light'
    ? 'mt-12 border border-teal-400/24 bg-white/34 p-6 text-sky-950 shadow-[0_0_40px_rgba(20,184,166,0.1),inset_0_1px_0_rgba(255,255,255,0.32)] backdrop-blur-2xl'
    : 'mt-12 border border-orange-300/20 bg-black/24 p-6 text-orange-50 shadow-[0_0_40px_rgba(249,115,22,0.1),inset_0_1px_0_rgba(255,237,213,0.08)] backdrop-blur-2xl';
  const creativeSpecialtyChipClass = theme === 'light'
    ? 'border border-teal-400/28 bg-teal-50/80 px-3 py-1 text-sm text-sky-950/70'
    : 'border border-orange-300/25 bg-orange-500/10 px-3 py-1 text-sm text-orange-100/75';
  const creativeLaunchButtonClass = theme === 'light'
    ? 'border border-teal-400/45 bg-white/75 px-12 py-5 text-2xl font-black uppercase tracking-[0.12em] text-teal-800 shadow-[0_0_44px_rgba(20,184,166,0.22)] backdrop-blur-sm transition-all duration-300 hover:border-teal-500/75 hover:bg-teal-50'
    : 'border border-orange-300/40 bg-orange-500/15 px-12 py-5 text-2xl font-black uppercase tracking-[0.12em] text-orange-50 shadow-[0_0_44px_rgba(249,115,22,0.24)] backdrop-blur-sm transition-all duration-300 hover:border-orange-200/80 hover:bg-orange-500/25';

  const handleReturnHome = () => {
    setActiveOverlay('none');
    setShowBlackBg(false);
    setShowRocketVideo(false);
    setShowCreativeContent(false);
    setTimeout(() => {
      setCardsDropped(false);
    }, 800);
  };

  // Add a function to reset overlays
  const handleNavAndReset = () => {
    setActiveOverlay('none');
    setShowBlackBg(false);
    setCardsDropped(false);
    setShowRocketVideo(false);
    setShowCreativeContent(false);
    setIsMobileMenuOpen(false);
  };

  React.useEffect(() => {
    if (!isSupabaseConfigured) return;

    supabase.auth.getSession().then(({ data }) => {
      const user = data.session?.user;
      setIsClientLoggedIn(Boolean(user));
      setClientName(user?.user_metadata?.full_name || user?.email?.split('@')[0] || '');
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user;
      setIsClientLoggedIn(Boolean(user));
      setClientName(user?.user_metadata?.full_name || user?.email?.split('@')[0] || '');
    });

    return () => subscription.unsubscribe();
  }, []);

  React.useEffect(() => {
    const handleCartUpdate = (event) => {
      setSiteCart(event.detail || null);
      if (event.detail) {
        window.localStorage.setItem('ddd-site-cart', JSON.stringify(event.detail));
      } else {
        window.localStorage.removeItem('ddd-site-cart');
      }
    };

    const handleOpenCart = () => setIsCartOpen(true);

    window.addEventListener('ddd:update-cart', handleCartUpdate);
    window.addEventListener('ddd:open-cart', handleOpenCart);

    return () => {
      window.removeEventListener('ddd:update-cart', handleCartUpdate);
      window.removeEventListener('ddd:open-cart', handleOpenCart);
    };
  }, []);

  React.useEffect(() => {
    if (typeof document === 'undefined') return;

    const themeColor = theme === 'light' ? '#f8fafc' : '#050505';
    const metas = document.querySelectorAll('meta[name="theme-color"]');

    if (metas.length) {
      metas.forEach((meta) => meta.setAttribute('content', themeColor));
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      meta.setAttribute('content', themeColor);
      document.head.appendChild(meta);
    }
  }, [theme]);

  const handleQuickLogout = async () => {
    if (!isSupabaseConfigured) return;
    await supabase.auth.signOut();
    setIsClientLoggedIn(false);
    setClientName('');
    handleNavAndReset();
  };

  const openAuthModal = (mode = 'login') => {
    setAuthModalMode(mode);
    setIsAuthModalOpen(true);
    setIsMobileMenuOpen(false);
  };

  React.useEffect(() => {
    const handleOpenAuth = (event) => {
      openAuthModal(event.detail?.mode || 'login');
    };

    window.addEventListener('ddd:open-auth', handleOpenAuth);
    return () => window.removeEventListener('ddd:open-auth', handleOpenAuth);
  }, []);

  React.useEffect(() => {
    let timer;
    if (cardsDropped) {
      timer = setTimeout(() => setShowBlackBg(true), 600);
    } else {
      setShowBlackBg(false);
    }
    return () => clearTimeout(timer);
  }, [cardsDropped]);

  React.useEffect(() => {
    let timer;
    if (showBlackBg) {
      timer = setTimeout(() => {
        if (activeOverlay === 'cleanCode' || activeOverlay === 'creativeDesign') {
          // already set, do nothing
        } else if (cardsDropped) {
          // fallback: if cardsDropped but no overlay, do nothing
        }
      }, 300);
    }
    return () => clearTimeout(timer);
  }, [showBlackBg, activeOverlay, cardsDropped]);

  return (
    <BrowserRouter>
      <RouteSEO />
             <div className="min-h-screen w-full relative overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Rocket launch video background */}
      {showRocketVideo && (
        <video
          autoPlay
          muted
          playsInline
          className="fixed inset-0 w-full h-full object-cover object-right md:object-center z-50"
          style={{ pointerEvents: 'none' }}
        >
          <source src="/Rocket launch.mp4" type="video/mp4" />
        </video>
      )}
      
             {/* Creative Design Content - appears after 5.5 seconds */}
       {showCreativeContent && activeOverlay === 'creativeDesign' && (
         <motion.div
           initial={{ opacity: 0, y: 20 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.8, ease: "easeInOut" }}
           className="fixed inset-0 z-[100] flex flex-col items-center justify-start overflow-y-auto p-4 md:justify-center"
           style={{
             background: theme === 'light'
               ? 'linear-gradient(135deg, rgba(248,250,252,0.08) 0%, rgba(236,254,255,0.05) 48%, rgba(20,184,166,0.06) 100%)'
               : 'linear-gradient(135deg, rgba(5,5,5,0.08) 0%, rgba(11,11,13,0.05) 48%, rgba(249,115,22,0.06) 100%)',
             paddingTop: contentTopOffsetPx ? `${contentTopOffsetPx}px` : undefined,
           }}
         >
          {/* Return Button */}
          <motion.button
             onClick={handleReturnHome}
             className={moduleReturnButtonClass}
             style={{ top: contentTopOffsetPx ? `${contentTopOffsetPx}px` : undefined }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Return
          </motion.button>
                     <div className="mt-6 max-w-5xl text-center md:mt-0" style={{ color: moduleTextColor }}>
            <motion.h2 
              className={creativeTitleClass}
              style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Creative Design
            </motion.h2>
            <motion.p 
              className={`mb-8 text-xl md:text-2xl ${moduleBodyTextClass}`}
              style={{ fontFamily: 'Century Gothic, Arial, sans-serif' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Innovative design solutions that launch your brand to new heights
            </motion.p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
              <motion.div 
                className={creativeCardClass}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 1.0, duration: 0.6, ease: "easeOut" }}
              >
                <div className={creativeIconClass}>
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-black">Creative Concepts</h3>
                <p className={creativeMutedTextClass}>Original ideas that capture attention and spark imagination</p>
              </motion.div>
              
              <motion.div 
                className={creativeCardClass}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 1.15, duration: 0.6, ease: "easeOut" }}
              >
                <div className={creativeIconClass}>
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 11v5a2 2 0 002 2h6a2 2 0 002-2v-5M7 4h10l-1 9H8L7 4z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-black">Custom Theme Design</h3>
                <p className={creativeMutedTextClass}>Bespoke design themes tailored perfectly to your brand's unique identity</p>
              </motion.div>
              
              <motion.div 
                className={creativeCardClass}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 1.3, duration: 0.6, ease: "easeOut" }}
              >
                <div className={creativeIconClass}>
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-black">Dynamic Animations</h3>
                <p className={creativeMutedTextClass}>Motion graphics and animations that bring your ideas to life</p>
              </motion.div>
              
              <motion.div 
                className={creativeCardClass}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 1.45, duration: 0.6, ease: "easeOut" }}
              >
                <div className={creativeIconClass}>
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-black">Brand Innovation</h3>
                <p className={creativeMutedTextClass}>Cutting-edge design strategies that set your brand apart</p>
              </motion.div>
              
              <motion.div 
                className={creativeCardClass}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 1.6, duration: 0.6, ease: "easeOut" }}
              >
                <div className={creativeIconClass}>
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-black">Visual Storytelling</h3>
                <p className={creativeMutedTextClass}>Compelling narratives told through powerful visual design</p>
              </motion.div>
              
              <motion.div 
                className={creativeCardClass}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 1.75, duration: 0.6, ease: "easeOut" }}
              >
                <div className={creativeIconClass}>
                  <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <h3 className="mb-2 text-lg font-black">Award-Winning Design</h3>
                <p className={creativeMutedTextClass}>Premium design solutions that exceed expectations</p>
              </motion.div>
            </div>
            
            <motion.div 
              className={creativeSpecialtyPanelClass}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 2.0, duration: 0.8, ease: "easeOut" }}
            >
              <h3 className="mb-4 text-xl font-black">Creative Specialties</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {['Business Cards', 'Billboard Design', '2D Animation', 'Logo Design', 'Brand Identity', 'Corporate Catalogues', 'Digital Illustration', 'Motion Graphics'].map((specialty) => (
                  <span key={specialty} className={creativeSpecialtyChipClass}>
                    {specialty}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
      {!isProductRoute && <Meteors number={20} theme={theme} />}
      {/* Navigation Bar */}
      <nav className={`fixed top-0 left-0 right-0 z-[150] border-b-0 backdrop-blur-md ${isLightHome ? 'bg-white/65 shadow-[0_14px_60px_rgba(8,47,73,0.06)]' : 'bg-black/30'}`}>
        <div className={`pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent ${isLightHome ? 'via-teal-400/80 shadow-[0_0_18px_rgba(20,184,166,0.55)]' : 'via-orange-200/80 shadow-[0_0_18px_rgba(249,115,22,0.65)]'} to-transparent`} />
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="relative w-12 h-12">
                <img 
                  src="/DDDlogo.png" 
                  alt="Design.dotdev Logo" 
                  className={`w-12 h-12 rounded-lg transition-opacity duration-500 ${useTealLogo ? 'opacity-0' : 'opacity-100'}`}
                />
                <img 
                  src="/DDDlogo Dark.png" 
                  alt="Design.dotdev Logo Dark" 
                  className={`absolute top-0 left-0 w-12 h-12 rounded-lg transition-opacity duration-500 ${useTealLogo ? 'opacity-100' : 'opacity-0'}`}
                />
              </div>
              <span className="transition-colors duration-500 font-semibold text-xl" style={{ color: navTextColor }}>Design<span className="nav-logo-dot" style={{ color: theme === 'light' ? '#0f766e' : '#f97316' }}>dot</span>dev</span>
            </div>
            
            {/* Navigation Links */}
                         <div className="hidden md:flex items-center space-x-8">
                             <Link to="/" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium group relative" style={{ color: navTextColor }}>
                <span className="relative z-10">Home</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-400 to-purple-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
              <Link to="/about" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium group relative" style={{ color: navTextColor }}>
                <span className="relative z-10">About</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-400 to-pink-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
              <Link to="/services" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium group relative" style={{ color: navTextColor }}>
                <span className="relative z-10">Services</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-pink-400 to-red-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
              <Link to="/portfolio" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium group relative" style={{ color: navTextColor }}>
                <span className="relative z-10">Portfolio</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-red-400 to-orange-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
              <Link to="/product" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium group relative" style={{ color: navTextColor }}>
                <span className="relative z-10">Packages</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-400 to-amber-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
              <Link to="/contact" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium group relative" style={{ color: navTextColor }}>
                <span className="relative z-10">Contact</span>
                <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-orange-400 to-yellow-500 group-hover:w-full transition-all duration-300"></div>
              </Link>
             </div>
                         {/* Theme Toggle Button */}
                           <button 
                onClick={toggleTheme}
                className="flex items-center justify-center w-10 h-10 rounded-full transition-colors duration-500 border mr-4"
                style={{ 
                  backgroundColor: useDarkNavControls ? 'rgba(249,115,22,0.14)' : isLightHome ? 'rgba(255,255,255,0.72)' : 'var(--overlay-bg)',
                  borderColor: useDarkNavControls ? 'rgba(251,146,60,0.38)' : isLightHome ? 'rgba(20,184,166,0.32)' : 'var(--border-primary)',
                  color: navTextColor
                }}
              >
                {theme === 'light' ? (
                  <IconMoon size={20} style={{ color: navTextColor }} />
                ) : (
                  <IconSun size={20} style={{ color: navTextColor }} />
                )}
              </button>
             
             {/* CTA Button */}
             <div className="hidden md:flex items-center gap-2">
               <button
                 type="button"
                 onClick={() => setIsCartOpen(true)}
                 className={useDarkNavControls
                   ? "relative rounded-full border border-orange-300/40 bg-orange-500/15 px-4 py-2 text-sm font-black text-white shadow-[0_0_20px_rgba(249,115,22,0.18)] transition-colors hover:border-orange-200/70"
                   : "relative rounded-full border border-black/10 bg-white/10 px-4 py-2 text-sm font-black text-black transition-colors hover:border-orange-400/60"}
               >
                 Cart
                 {siteCart?.addOns?.length ? (
                   <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-400 px-1 text-[10px] text-black">
                     {siteCart.addOns.length}
                   </span>
                 ) : null}
               </button>
               {isClientLoggedIn ? (
                 <Link
                   to="/client"
                   onClick={handleNavAndReset}
                   className={useDarkNavControls
                     ? "bg-orange-500/15 text-white px-5 py-2 rounded-full font-medium transition-colors duration-500 border border-orange-300/40 shadow-[0_0_20px_rgba(249,115,22,0.18)] hover:border-orange-200/70"
                     : "bg-white/10 text-black px-5 py-2 rounded-full font-medium transition-colors duration-500 border border-black/10 hover:border-orange-400/60"}
                 >
                   {`Hi, ${clientName || 'client'}`}
                 </Link>
               ) : (
                 <button
                   type="button"
                   onClick={() => openAuthModal('login')}
                   className={useDarkNavControls
                     ? "bg-orange-500/15 text-white px-5 py-2 rounded-full font-medium transition-colors duration-500 border border-orange-300/40 shadow-[0_0_20px_rgba(249,115,22,0.18)] hover:border-orange-200/70"
                     : "bg-white/10 text-black px-5 py-2 rounded-full font-medium transition-colors duration-500 border border-black/10 hover:border-orange-400/60"}
                 >
                   Login
                 </button>
               )}
               {isClientLoggedIn && (
                 <button
                   type="button"
                   onClick={handleQuickLogout}
                   className={useDarkNavControls
                     ? "rounded-full border border-white/15 bg-white/5 px-3 py-2 text-xs font-bold uppercase tracking-wider text-white/75 transition-colors hover:text-white hover:border-orange-300/60"
                     : "rounded-full border border-black/10 bg-white/40 px-3 py-2 text-xs font-bold uppercase tracking-wider text-black/65 transition-colors hover:text-black hover:border-orange-400/60"}
                 >
                   Logout
                 </button>
               )}
             </div>

                         {/* Mobile Menu Button */}
             <button
               className="md:hidden p-2 transition-colors duration-500"
              style={{ color: navTextColor }}
               onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
             >
               {isMobileMenuOpen ? <IconX size={24} /> : <IconMenu2 size={24} />}
             </button>
          </div>
                     {isMobileMenuOpen && (
             <div className="md:hidden mt-4 flex flex-col space-y-4">
               <Link to="/" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium" style={{ color: navTextColor }}>
                 Home
               </Link>
               <Link to="/about" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium" style={{ color: navTextColor }}>
                 About
               </Link>
               <Link to="/services" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium" style={{ color: navTextColor }}>
                 Services
               </Link>
               <Link to="/portfolio" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium" style={{ color: navTextColor }}>
                 Portfolio
               </Link>
               <Link to="/product" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium" style={{ color: navTextColor }}>
                 Packages
               </Link>
               <Link to="/contact" onClick={handleNavAndReset} className="transition-colors duration-500 font-medium" style={{ color: navTextColor }}>
                 Contact
               </Link>
               <button
                 type="button"
                 onClick={() => {
                   setIsCartOpen(true);
                   setIsMobileMenuOpen(false);
                 }}
                 className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full font-medium transition-colors duration-500 border border-white/20 hover:border-white/40 text-left"
                 style={{ color: navTextColor }}
               >
                 Cart{siteCart?.addOns?.length ? ` (${siteCart.addOns.length})` : ''}
               </button>
               {isClientLoggedIn ? (
                 <Link to="/client" onClick={handleNavAndReset} className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full font-medium transition-colors duration-500 border border-white/20 hover:border-white/40" style={{ color: navTextColor }}>
                   {`Hi, ${clientName || 'client'}`}
                 </Link>
               ) : (
                 <button
                   type="button"
                   onClick={() => openAuthModal('login')}
                   className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full font-medium transition-colors duration-500 border border-white/20 hover:border-white/40 text-left"
                   style={{ color: navTextColor }}
                 >
                   Login
                 </button>
               )}
               {isClientLoggedIn && (
                 <button
                   type="button"
                   onClick={handleQuickLogout}
                   className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full font-medium transition-colors duration-500 border border-white/20 hover:border-white/40 text-left"
                   style={{ color: navTextColor }}
                 >
                   Logout
                 </button>
               )}
             </div>
           )}
        </div>
      </nav>
      <AuthModal
        isOpen={isAuthModalOpen}
        initialMode={authModalMode}
        onClose={() => setIsAuthModalOpen(false)}
      />
      <CartDrawer cart={siteCart} isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      <Routes>
        <Route path="/" element={
          <main
            className="relative min-h-screen overflow-hidden px-6 pt-20 text-orange-50"
            style={homeShellStyle}
          >
            <div className={`pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full blur-3xl ${homeGlowClass}`} />
            <div className="relative z-10 max-w-7xl mx-auto">
              {/* Hero Content */}
              <div className="text-center pt-8 pb-20 md:pt-10 md:pb-24">
                <motion.div
                  className={homeBadgeClass}
                  initial={{ opacity: 0, y: 8 }}
                  animate={cardsDropped ? { opacity: 0, y: -8 } : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: 'easeOut' }}
                >
                  Bespoke web systems
                </motion.div>
                <motion.h1
                   className="pointer-events-none text-6xl md:text-8xl font-black mb-6 leading-tight"
                   style={{
                     letterSpacing: '-4px',
                     color: 'var(--text-primary)',
                     textShadow: theme === 'light' ? '0 0 38px rgba(20,184,166,0.18)' : '0 0 42px rgba(249,115,22,0.2)',
                   }}
                   initial={{ x: 0, opacity: 1 }}
                   animate={cardsDropped ? { x: -200, opacity: 0 } : { x: 0, opacity: 1 }}
                   transition={{ duration: 0.6, ease: 'easeIn' }}
                 >
                  Design<span style={{ color: 'var(--accent-primary)' }}>Dot</span>Dev
                 </motion.h1>
                                 <motion.p
                   className="pointer-events-none text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
                   style={{ color: 'var(--text-secondary)' }}
                   initial={{ x: 0, opacity: 1 }}
                   animate={cardsDropped ? { x: 200, opacity: 0 } : { x: 0, opacity: 1 }}
                   transition={{ duration: 0.6, ease: 'easeIn' }}
                 >
                  DesignDotDev is a web design and development studio in South Africa. We craft fast, accessible, and beautiful digital experiences for startups and SMEs.
                 </motion.p>
                <motion.div
                  className="pointer-events-none mx-auto mb-10 flex max-w-3xl flex-wrap justify-center gap-3"
                  initial={{ opacity: 0, y: 10 }}
                  animate={cardsDropped ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: 'easeOut' }}
                >
                  {['Design', 'Development', 'SEO', 'Web apps'].map((item) => (
                    <span
                      key={item}
                      className={homeChipClass}
                    >
                      {item}
                    </span>
                  ))}
                </motion.div>
                <div className="mx-auto grid max-w-[58rem] md:grid-cols-3 gap-4 mt-4 mb-0">
                  <AnimatePresence>
                    {!cardsDropped && (
                      <>
                        <CardSpotlight
                          theme={theme}
                          className={homeSpotlightClass}
                          initial={{ y: 0, opacity: 1 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: 80, opacity: 0 }}
                          transition={{ duration: 0.6, ease: 'easeIn' }}
                        >
                          <div
                            className="group/home relative flex h-full flex-col items-center justify-center overflow-hidden backdrop-blur-xl rounded-none p-8 text-center border transition-all duration-300 cursor-pointer home-card shadow-[0_0_34px_rgba(249,115,22,0.08)]"
                            style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}
                            onClick={() => {
                              setActiveOverlay('cleanCode');
                              setCardsDropped(true);
                            }}
                          >
                            <div className={homeCardLineClass} />
                            <div className={homeCardWashClass} />
                            <div className="relative z-10 w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(to right, var(--accent-primary), var(--accent-secondary))', boxShadow: homeIconShadow }}>
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#050505' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                              </svg>
                            </div>
                            <h3 className="relative z-10 text-xl font-black mb-4" style={{ color: 'var(--text-primary)' }}>Clean Code</h3>
                            <p className="relative z-10 leading-relaxed card-description" style={{ color: 'var(--text-secondary)' }}>
                              Robust, scalable code that performs flawlessly and grows with your business needs.
                            </p>
                          </div>
                        </CardSpotlight>
                        <CardSpotlight
                          theme={theme}
                          className={homeSpotlightClass}
                          initial={{ y: 0, opacity: 1 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: 80, opacity: 0 }}
                          transition={{ duration: 0.6, ease: 'easeIn' }}
                        >
                          <div
                            className="group/home relative flex h-full flex-col items-center justify-center overflow-hidden backdrop-blur-xl rounded-none p-8 text-center border transition-all duration-300 cursor-pointer home-card shadow-[0_0_34px_rgba(249,115,22,0.08)]"
                            style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}
                            onClick={() => {
                              setActiveOverlay('creativeDesign');
                              setCardsDropped(true);
                            }}
                          >
                            <div className={homeCardLineClass} />
                            <div className={homeCardWashClass} />
                            <div className="relative z-10 w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(to right, var(--accent-primary), var(--accent-secondary))', boxShadow: homeIconShadow }}>
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#050505' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                            </div>
                            <h3 className="relative z-10 text-xl font-black mb-4" style={{ color: 'var(--text-primary)' }}>Creative Design</h3>
                            <p className="relative z-10 leading-relaxed card-description" style={{ color: 'var(--text-secondary)' }}>
                              Innovative design solutions that capture your brand's essence and connect with your audience.
                            </p>
                          </div>
                        </CardSpotlight>
                        <CardSpotlight
                          theme={theme}
                          className={homeSpotlightClass}
                          initial={{ y: 0, opacity: 1 }}
                          animate={{ y: 0, opacity: 1 }}
                          exit={{ y: 80, opacity: 0 }}
                          transition={{ duration: 0.6, ease: 'easeIn' }}
                        >
                          <div
                            className="group/home relative flex h-full flex-col items-center justify-center overflow-hidden backdrop-blur-xl rounded-none p-8 text-center border transition-all duration-300 cursor-pointer home-card shadow-[0_0_34px_rgba(249,115,22,0.08)]"
                            style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}
                            onClick={() => {
                              setActiveOverlay('lightningFast');
                              setCardsDropped(true);
                            }}
                          >
                            <div className={homeCardLineClass} />
                            <div className={homeCardWashClass} />
                            <div className="relative z-10 w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(to right, var(--accent-primary), var(--accent-secondary))', boxShadow: homeIconShadow }}>
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#050505' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                            </div>
                            <h3 className="relative z-10 text-xl font-black mb-4" style={{ color: 'var(--text-primary)' }}>Lightning Fast</h3>
                            <p className="relative z-10 leading-relaxed card-description" style={{ color: 'var(--text-secondary)' }}>
                              Optimized performance that ensures your users enjoy a seamless, fast experience.
                            </p>
                          </div>
                        </CardSpotlight>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </main>
        } />
        <Route path="/services" element={<ServicesPage setVortexActive={setVortexActive} />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/portfolio" element={<PortfolioPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/product" element={<ProductPage />} />
        <Route path="/order" element={<ProductPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/client" element={<ClientPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Black background overlay when showBlackBg is true */}
      <AnimatePresence>
        {showBlackBg && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: 'easeInOut' }}
              style={{ position: 'fixed', inset: 0, zIndex: 10, background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 50%, #f9fafb 100%)', pointerEvents: 'none' }}
            />

          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeOverlay === 'cleanCode' && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
                         className="fixed inset-0 z-30 flex flex-col items-center overflow-y-auto justify-start pt-32 pb-32 md:justify-start md:pt-32 md:pb-32"
                           style={{
                pointerEvents: 'auto',
                color: moduleTextColor,
                background: moduleOverlayBackground,
              }}
          >
            {/* Return Button - Near Get Started Button */}
            <motion.button
              onClick={handleReturnHome}
              className={moduleReturnButtonClass}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
            >
              <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return
            </motion.button>

            {/* Side-by-side layout for Compare and Text */}
            <div className="flex flex-col md:flex-row items-center justify-center w-full max-w-6xl px-2 md:px-0 gap-8 md:gap-20">
              {/* Compare Component on the left, more left-aligned */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
                className="relative flex-shrink-0 md:mr-8 lg:mr-12"
              >
                <div className={moduleFrameClass} />
                <div className={moduleFrameLineClass} />
                                 <Compare
                   firstImage="/blackrose.png"
                   secondImage="/redrose.png"
                  className={moduleCompareClass}
                  firstImageClassName="!rounded-none object-cover"
                  secondImageClassname="!rounded-none object-cover"
                   slideMode="drag"
                   theme={theme}
                 />
              </motion.div>
              {/* Text Section on the right, wider */}
              <div className="flex-1 flex flex-col items-center md:items-start text-center md:text-left max-w-2xl md:max-w-3xl lg:max-w-4xl">
                                 <motion.h2
                   className={`mb-4 text-4xl font-black md:text-5xl ${moduleAccentTextClass}`}
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   exit={{ opacity: 0, y: -20 }}
                   transition={{ duration: 0.6, ease: 'easeInOut' }}
                 >
                   Clean Code
                 </motion.h2>
                <p className={`mb-4 w-full text-lg font-medium md:text-xl ${moduleBodyTextClass}`}>
                  Our commitment to clean code is unwavering. We believe that great software is built on a foundation of clarity, simplicity, and maintainability. Every line we write is crafted with precision, ensuring your project is robust, scalable, and easy to evolve. With best practices, thorough documentation, and a passion for excellence, we deliver solutions that stand the test of time and empower your business to grow.
                </p>
                <ul className={`w-full list-inside list-disc space-y-2 text-base ${moduleListTextClass}`}>
                  <li>Readable, well-structured code for easy collaboration</li>
                  <li>Scalable architecture to support your future needs</li>
                  <li>Comprehensive documentation and clear comments</li>
                  <li>Rigorous testing and quality assurance</li>
                  <li>Continuous improvement and code reviews</li>
                </ul>
              </div>
            </div>

                        {/* Second section with reversed layout - Text on left, Compare on right */}
            <div className="flex flex-col md:flex-row items-center justify-center w-full max-w-6xl px-2 md:px-0 gap-8 md:gap-20 mt-32">
              {/* Text Section on the left, wider */}
              <div className="flex-1 flex flex-col items-center md:items-start text-center md:text-left max-w-2xl md:max-w-3xl lg:max-w-4xl">
                                 <motion.h2
                  className={`mb-4 text-4xl font-black md:text-5xl ${moduleAccentTextClass}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.6, ease: 'easeInOut' }}
                >
                  Future-Proof Solutions
                </motion.h2>
                <p className={`mb-4 w-full text-lg font-medium md:text-xl ${moduleBodyTextClass}`}>
                  We build digital solutions that evolve with your business. Our clean, modular approach ensures your website remains adaptable, secure, and high-performing as technology advances. Every decision is made with longevity in mind, creating digital assets that grow alongside your success and continue delivering value for years to come.
                </p>
                <ul className={`w-full list-inside list-disc space-y-2 text-base ${moduleListTextClass}`}>
                  <li>Modern frameworks built for long-term stability</li>
                  <li>Flexible architecture that adapts to changing needs</li>
                  <li>Security-first development protecting your investment</li>
                  <li>Performance optimization for sustained excellence</li>
                  <li>Strategic planning for seamless future upgrades</li>
                </ul>
              </div>
                            {/* Compare Component on the right, more right-aligned */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
                className="relative flex-shrink-0 md:ml-8 lg:ml-12"
              >
                <div className={moduleFrameClass} />
                <div className={moduleFrameLineClass} />
                                 <Compare
                  firstImage="/Tealbutterfly.png"
                  secondImage="/Pinkbutterfly.png"
                  className={moduleCompareClass}
                  firstImageClassName="!rounded-none object-cover"
                  secondImageClassname="!rounded-none object-cover"
                  slideMode="drag"
                  theme={theme}
                />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeOverlay === 'creativeDesign' && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            className="fixed inset-0 z-30 bg-cover bg-no-repeat bg-right md:bg-center"
            style={{
              pointerEvents: 'auto',
              backgroundImage:
                `${theme === 'light'
                  ? 'linear-gradient(135deg, rgba(248,250,252,0.48) 0%, rgba(236,254,255,0.26) 46%, rgba(20,184,166,0.18) 100%)'
                  : 'linear-gradient(135deg, rgba(5,5,5,0.34) 0%, rgba(9,8,7,0.18) 46%, rgba(23,16,11,0.44) 100%)'}, url(/rocketshippixelbg.png)`,
            }}
          >
            {/* Title at top center */}
                                     
            {/* Button dead center */}
            {!showRocketVideo && !showCreativeContent && (
                             <motion.button
                 className={creativeLaunchButtonClass}
                style={{ 
                  position: 'fixed',
                  top: 'calc(50vh - 40px)',
                  left: 'calc(50vw - 120px)',
                  fontFamily: 'Century Gothic, Arial, sans-serif'
                }}

                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 1.2, ease: 'easeOut', delay: 0.5 }}
                whileHover={{ boxShadow: theme === 'light' ? '0 25px 50px -12px rgba(20, 184, 166, 0.42)' : '0 25px 50px -12px rgba(249, 115, 22, 0.5)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowRocketVideo(true);
                  // Start content timer
                  setTimeout(() => {
                    console.log('Setting showCreativeContent to true');
                    setShowCreativeContent(true);
                  }, 1000);
                }}
              >
                <span className="flex items-center justify-center gap-2">
                  <img src="/rocket.png" alt="Rocket" className="w-6 h-6" />
                  Launch
                </span>
              </motion.button>
            )}
            {/* Return Button - Near Get Started Button */}
            <motion.button
              onClick={handleReturnHome}
              className={moduleReturnButtonClass}
              style={{ top: contentTopOffsetPx ? `${contentTopOffsetPx}px` : undefined }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
            >
              <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return
            </motion.button>

            {/* Centered content area - now clean */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            >
              <div className="w-[600px] h-[500px] flex items-center justify-center">
                {/* Clean space - no badge */}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeOverlay === 'lightningFast' && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 30,
              background: moduleOverlayBackground,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pointerEvents: 'auto',
              color: moduleTextColor,
            }}
            className="overflow-y-auto"
          >
            {/* Return Button - Near Get Started Button */}
            <motion.button
              onClick={handleReturnHome}
              className={moduleReturnButtonClass}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
            >
              <svg className="w-5 h-5 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return
            </motion.button>
            <div className="flex w-full flex-col items-center px-4 pb-12 pt-24 md:px-0">
              <div className="relative mb-6 flex h-[200px] w-full max-w-3xl items-center justify-center">
                <div className={moduleHoverGlowClass} />
                <TextHoverEffect text="Lightning Fast" duration={0.5} theme={theme} />
              </div>
              <div className={modulePanelClass}>
                <div className={modulePanelLineClass} />
                <div className={modulePanelWashClass} />
                <div className="relative z-10">
                                 <h2 className={`mb-4 text-left text-2xl font-black md:text-3xl ${moduleAccentTextClass}`}>Unmatched Speed & Performance</h2>
                <p className={`mb-2 text-left text-lg font-medium md:text-xl ${moduleBodyTextClass}`}>
                  Our websites and apps are engineered for speed. We leverage the latest technologies, best practices, and performance optimizations to ensure your users enjoy instant load times and seamless interactions—on any device, anywhere in the world.
                </p>
                <ul className={`max-w-lg list-inside list-disc space-y-2 text-left text-base ${moduleListTextClass}`}>
                  <li>Lightning-fast page loads and smooth navigation</li>
                  <li>Optimized assets and code for peak efficiency</li>
                  <li>Progressive enhancement for all devices</li>
                  <li>Continuous performance monitoring and tuning</li>
                  <li>Delight your users and boost your SEO</li>
                </ul>
                <div className="relative mt-8 flex h-[200px] w-full max-w-3xl items-center justify-center">
                  <div className={moduleHoverGlowClass} />
                  <TextHoverEffectSEO text="Optimized SEO" duration={0.5} theme={theme} />
                </div>
                <div className="mt-5 w-full">
                                     <h3 className={`mb-2 text-left text-xl font-black md:text-2xl ${moduleAccentTextClass}`}>SEO Excellence, Engineered</h3>
                  <p className={`mb-2 text-left text-lg font-medium ${moduleBodyTextClass}`}>
                    We implement industry-leading SEO strategies to ensure your website ranks at the top. Our approach covers every detail:
                  </p>
                  <ul className={`list-inside list-disc space-y-2 text-left text-base ${moduleListTextClass}`}>
                                         <li><span className={`font-black ${theme === 'light' ? 'text-teal-700' : 'text-orange-300'}`}>Best SEO Practices:</span> From semantic HTML to structured data, we build every page with search engines and users in mind. Our code is clean, accessible, and optimized for maximum visibility.</li>
                     <li><span className={`font-black ${theme === 'light' ? 'text-teal-700' : 'text-orange-300'}`}>Image & Site Optimization:</span> Every image is compressed, tagged, and delivered in next-gen formats. We tune site speed, mobile responsiveness, and Core Web Vitals for a flawless user experience and higher rankings.</li>
                     <li><span className={`font-black ${theme === 'light' ? 'text-teal-700' : 'text-orange-300'}`}>301 Redirects & Legacy SEO:</span> We seamlessly migrate and redirect old URLs, preserving your hard-earned SEO equity. Our meticulous redirect strategies ensure no ranking or traffic is lost during site updates or rebrands.</li>
                  </ul>
                  <p className={`mt-4 text-left text-lg font-medium ${moduleBodyTextClass}`}>
                    With us, your digital presence is not just beautiful and fast—it's discoverable, future-proof, and engineered for SEO dominance.
                  </p>
                </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </BrowserRouter>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root')
);
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <App />
    </ThemeProvider>
  </React.StrictMode>
); 